/**
 * Robbie Williams Storefront Ident
 *
 * Plays a full-screen centered image sequence on the homepage before revealing
 * the store. Configuration and frame data are supplied by storefront-ident.liquid
 * via a <script type="application/json"> tag.
 *
 * This module has no external imports and is loaded as a deferred ES module.
 */

const STORAGE_SESSION_KEY = 'rw-storefront-ident-played';
const STORAGE_DATE_KEY = 'rw-storefront-ident-date';

/** Durations (ms) that match the CSS transition values in storefront-ident.css */
const FRAME_CROSSFADE_MS = 80;
const CSS_LOGO_FADE_MS = 300;
const CSS_EXIT_FADE_MS = 400;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getTodayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getSessionFlag = () => {
  try {
    return sessionStorage.getItem(STORAGE_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const setSessionFlag = () => {
  try {
    sessionStorage.setItem(STORAGE_SESSION_KEY, 'true');
  } catch {
    // Storage unavailable; silently continue.
  }
};

const getStoredDate = () => {
  try {
    return localStorage.getItem(STORAGE_DATE_KEY);
  } catch {
    return null;
  }
};

const setStoredDate = (date) => {
  try {
    localStorage.setItem(STORAGE_DATE_KEY, date);
  } catch {
    // Storage unavailable; silently continue.
  }
};

const shouldPlay = (frequency, testMode) => {
  if (testMode) return true;

  switch (frequency) {
    case 'every_visit':
      return true;
    case 'once_per_session':
      return !getSessionFlag();
    case 'once_per_day':
      return getStoredDate() !== getTodayString();
    default:
      return true;
  }
};

const markPlayed = (frequency, testMode) => {
  if (testMode) return;

  switch (frequency) {
    case 'once_per_session':
      setSessionFlag();
      break;
    case 'once_per_day':
      setStoredDate(getTodayString());
      break;
  }
};

const shuffle = (array) => {
  const arr = [...array];

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
};

const decodeImage = (img) => {
  if (typeof img.decode === 'function') {
    return img.decode().catch(() => {});
  }

  return Promise.resolve();
};


const teardown = (overlay) => {
  overlay.classList.remove('rw-storefront-ident--playing');
  overlay.classList.add('rw-storefront-ident--exiting');
  document.body.classList.remove('rw-ident-locked');

  const cleanup = () => {
    if (overlay.isConnected) overlay.remove();
  };

  overlay.addEventListener('transitionend', cleanup, { once: true });
  // Safety fallback in case transitionend does not fire (e.g. hidden tab).
  setTimeout(cleanup, CSS_EXIT_FADE_MS + 100);
};

const playIdent = async (overlay, data) => {
  const { frames, frameDuration, logoHold, imageOrder, logoUrl, logoAlt, maskShape } = data;

  const orderedFrames = imageOrder === 'random' ? shuffle(frames) : frames;

  if (orderedFrames.length === 0) {
    overlay.remove();
    return;
  }

  const frameEl = overlay.querySelector('[data-rw-ident-frame]');
  const logoEl = overlay.querySelector('[data-rw-ident-logo]');
  const logoImgEl = overlay.querySelector('[data-rw-ident-logo-img]');

  if (!frameEl || !logoEl) {
    overlay.remove();
    return;
  }

  if (maskShape && maskShape !== 'none') {
    frameEl.dataset.mask = maskShape;
  }

  const frameMs = Math.max(50, Number(frameDuration) * 1000);
  const frameOverlapMs = Math.min(FRAME_CROSSFADE_MS, frameMs);
  const logoHoldMs = Math.max(0, Number(logoHold) * 1000);

  /*
   * Append every frame <img> into the overlay while it is still transparent
   * (opacity: 0, NOT display: none). Because the overlay is already in the
   * compositor tree, the browser uploads GPU textures for every image during
   * the decode() call below. When playback starts, all textures are resident
   * before the short frame crossfades begin.
   */
  const frameImgs = orderedFrames.map((frame) => {
    const img = document.createElement('img');
    img.sizes = frame.sizes || '';
    img.srcset = frame.srcset || '';
    img.src = frame.url;
    img.alt = frame.alt || '';
    img.className = 'rw-storefront-ident__frame-img';
    frameEl.appendChild(img);
    return img;
  });

  // Decode all frames in parallel; allSettled keeps going if one fails.
  await Promise.allSettled(frameImgs.map((img) => decodeImage(img)));

  // Kick off logo preload while the sequence plays.
  if (logoUrl) {
    const logoPreload = new Image();
    logoPreload.src = logoUrl;
  }

  if (!overlay.isConnected) return;

  // Reveal the overlay and lock scroll.
  overlay.classList.add('rw-storefront-ident--playing');
  document.body.classList.add('rw-ident-locked');

  const [firstImg, ...remainingImgs] = frameImgs;
  let currentImg = firstImg;

  if (!currentImg) {
    teardown(overlay);
    return;
  }

  currentImg.classList.add('rw-storefront-ident__frame-img--visible');

  for (const nextImg of remainingImgs) {
    if (!overlay.isConnected) return;

    await sleep(Math.max(0, frameMs - frameOverlapMs));

    if (!overlay.isConnected) return;

    nextImg.classList.add('rw-storefront-ident__frame-img--visible');
    await sleep(frameOverlapMs);

    currentImg.classList.remove('rw-storefront-ident__frame-img--visible');
    currentImg = nextImg;
  }

  await sleep(Math.max(0, frameMs - frameOverlapMs));
  currentImg.classList.remove('rw-storefront-ident__frame-img--visible');

  if (!overlay.isConnected) return;

  // Reveal logo with CSS fade, then hold.
  if (logoUrl && logoImgEl instanceof HTMLImageElement) {
    logoImgEl.src = logoUrl;
    logoImgEl.alt = logoAlt || '';
    await decodeImage(logoImgEl);
  }

  if (!overlay.isConnected) return;

  logoEl.classList.add('rw-storefront-ident__logo--visible');
  await sleep(CSS_LOGO_FADE_MS + logoHoldMs);

  teardown(overlay);
};

const init = () => {
  const overlay = document.getElementById('rw-storefront-ident');
  if (!overlay) return;

  const dataEl = document.getElementById('rw-storefront-ident-data');
  if (!dataEl) return;

  let data;

  try {
    data = JSON.parse(dataEl.textContent || '{}');
  } catch {
    overlay.remove();
    return;
  }

  const { frequency, testMode, frames } = data;

  // Skip entirely for visitors who prefer reduced motion. No storage flag is written.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    overlay.remove();
    return;
  }

  if (!frames || frames.length === 0) {
    overlay.remove();
    return;
  }

  if (!shouldPlay(frequency, testMode)) {
    overlay.remove();
    return;
  }

  // Record playback before starting so repeated rapid loads count as one play.
  markPlayed(frequency, testMode);

  // Defer one frame to allow page content to paint beneath the overlay.
  requestAnimationFrame(() => {
    playIdent(overlay, data);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
